import React, {useState} from 'react';
import {Box, Center, HStack, VStack, useToast} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import Icon from '../../../components/Icon';
import useAuth from '../../../hooks/useAuth';
import {
  useGetUserAddresses,
  useAddUserAddress,
  useUpdateUserAddress,
  useFetchAddressTypes,
} from '../../../hooks/useUserAddress';
import {fetchStateList} from '../../../endpoints/api';
import {find} from 'lodash';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import styled from 'styled-components';

const SaveButton = styled.button<{$disabled: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#E02F84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

interface EditNeighborhoodModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditNeighborhoodModal = ({isOpen, onClose}: EditNeighborhoodModalProps) => {
  const {user} = useAuth();
  const {userAddresses} = useGetUserAddresses({
    userId: user?.id,
    addressType: 'HOME',
  });
  const addAddress = useAddUserAddress();
  const updateAddress = useUpdateUserAddress();
  const fetchAddressTypes = useFetchAddressTypes();
  const addressTypes = fetchAddressTypes.data?.data;
  const toast = useToast();

  const existingAddress = userAddresses?.[0] as any;
  const [streetAddress, setStreetAddress] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (existingAddress?.street_address) {
      const parts: string[] = [];
      // Street address with unit
      parts.push(
        existingAddress.unit
          ? `${existingAddress.street_address} ${existingAddress.unit}`
          : existingAddress.street_address
      );
      // City, State ZIP
      const cityStateZip = [
        existingAddress.city?.name || existingAddress.city,
        existingAddress.state?.abbreviation || '',
        existingAddress.zip_code,
      ]
        .filter(Boolean)
        .join(' ');
      if (cityStateZip) parts.push(cityStateZip);
      setStreetAddress(parts.join(', '));
    }
  }, [existingAddress]);

  const handleSave = async () => {
    if (!selectedPlace || isSaving || !user) return;
    setIsSaving(true);

    try {
      const addressComponents = selectedPlace.address_components || [];
      let streetNumber = '';
      let route = '';
      let unit = '';
      let city = '';
      let state = '';
      let zipCode = '';

      for (const component of addressComponents) {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        } else if (types.includes('route')) {
          route = component.long_name;
        } else if (types.includes('subpremise')) {
          unit = component.long_name;
        } else if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      }

      const parsedStreetAddress = `${streetNumber} ${route}`.trim();
      if (!parsedStreetAddress || !city || !state || !zipCode) {
        toast({title: 'Incomplete address. Please select a full address from the dropdown.', status: 'error', duration: 3000});
        setIsSaving(false);
        return;
      }

      const statesResponse = await fetchStateList();
      const states = statesResponse.data;
      const stateData = states.find((s: any) => s.abbreviation === state);
      if (!stateData) {
        toast({title: `State "${state}" not found`, status: 'error', duration: 3000});
        setIsSaving(false);
        return;
      }

      const homeTypeId = find(addressTypes, {name: 'HOME'})?.id;
      if (!homeTypeId) {
        toast({title: 'Address type not found', status: 'error', duration: 3000});
        setIsSaving(false);
        return;
      }

      const addressData = {
        type_id: homeTypeId,
        street_address: parsedStreetAddress,
        unit: unit || '',
        city,
        state_id: stateData.id,
        zip_code: zipCode,
      };

      if (existingAddress?.id) {
        await updateAddress.mutateAsync({
          userId: user.id,
          addressId: existingAddress.id,
          address: addressData as any,
        });
      } else {
        await addAddress.mutateAsync({
          userId: user.id,
          address: addressData as any,
        });
      }
      toast({title: 'Address updated!', status: 'success', duration: 2000});
      onClose();
    } catch (err: any) {
      toast({title: 'Could not update address', status: 'error', duration: 3000});
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          Neighborhood
        </TextG14>
      }
      modalPadding="16px 20px 24px">
      <VStack spacing={5} align="stretch" minH="300px" justify="space-between">
        <Box>
          {/* Info box */}
          <Box bg="#FFF" borderRadius="14px" p="16px" mb={5} border="2px solid #E9E9E9" boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
            <TextG14 mt="0" color="#1A1A1A" lineHeight="20px" fontWeight="500">
              Tell us where you live, and we'll connect you with people and experiences right around
              the corner.
            </TextG14>
          </Box>

          {/* Street address input */}
          <Box>
            <Box fontSize="11px" fontFamily="Gordita" color="#757D84" mb={1} ml={1}>
              Street address
            </Box>
            <GooglePlacesAutocomplete
              value={streetAddress}
              onPlaceSelected={(place) => {
                setStreetAddress(place.formatted_address || '');
                setSelectedPlace(place);
              }}
              onAddressChange={(address) => {
                setStreetAddress(address);
                if (selectedPlace && address !== selectedPlace.formatted_address) {
                  setSelectedPlace(null);
                }
              }}
              placeholder="Enter your street address"
              autoFocus
              fontFamily="Gordita"
              fontSize="14px"
              lineHeight="20px"
              p="14px 16px"
              border="1px solid #e5e5e5"
              borderRadius="12px"
              _focus={{borderColor: '#1a1a1a'}}
            />
          </Box>

          {/* Privacy notice */}
          <HStack spacing={2} mt={4}>
            <Center w="40px" h="40px" bg="#D1F4D0" flexShrink={0} p="4px" borderRadius="8px">
              <Icon
                iconName="fi fi-sr-shield-check"
                style={{color: '#048400', fontSize: '26px', width: '26px', height: '26px'}}
              />
            </Center>
            <TextG14 mt="0" color="#262626" fontWeight="500">
              Your address is private! No one in the app will see it.
            </TextG14>
          </HStack>
        </Box>

        <SaveButton $disabled={!selectedPlace || isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

export default EditNeighborhoodModal;
